import { Link } from "react-router-dom";
import { ShieldAlert, ExternalLink, Clock, Ban, HeartHandshake, PhoneCall } from "lucide-react";
import { useSeo } from "@/lib/seo";

const RESOURCES = [
  { name: "BeGambleAware", desc: "Free, confidential advice and support for anyone affected by gambling.", url: "https://www.begambleaware.org", region: "UK / Intl" },
  { name: "GAMSTOP", desc: "Free self-exclusion scheme that blocks access to online gambling in Great Britain.", url: "https://www.gamstop.co.uk", region: "UK" },
  { name: "GamCare", desc: "Support, information and counselling for problem gamblers via the National Gambling Helpline.", url: "https://www.gamcare.org.uk", region: "UK" },
  { name: "Gambling Therapy", desc: "Free online support to people affected by problem gambling worldwide.", url: "https://www.gamblingtherapy.org", region: "International" },
];

const SIGNS = [
  "Betting more than you can afford to lose",
  "Chasing losses to win money back",
  "Gambling to escape stress or low mood",
  "Lying to others about how much you gamble",
  "Borrowing money or selling things to fund play",
  "Feeling anxious, guilty or unable to stop",
];

export default function ResponsibleGambling() {
  useSeo({
    title: "Responsible Gambling — 18+ | Licence to Play",
    description:
      "Gamble responsibly. 18+ only. Recognise the signs of problem gambling and find support: BeGambleAware, GAMSTOP, GamCare and Gambling Therapy.",
    keywords: "responsible gambling, problem gambling, gamble aware, gamstop, self exclusion",
  });

  return (
    <div>
      <section className="border-b border-white/10 bg-crimson/10 relative grain-overlay">
        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 py-16">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-12 h-12 grid place-items-center border-2 border-crimson-bright text-crimson-bright font-mono font-bold text-lg">18+</span>
            <span className="overline text-crimson-bright">Strictly for adults</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-white tracking-tighter">Responsible Gambling</h1>
          <p className="mt-5 text-lg text-white/70 max-w-2xl leading-relaxed">
            Licence to Play exists to educate and entertain. Gambling should always be fun — never a way to
            make money or escape problems. If it stops being fun, it's time to stop.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-14 space-y-14">
        {/* Principles */}
        <section>
          <p className="overline text-gold mb-6">▸ The Ground Rules</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: <ShieldAlert className="w-5 h-5" />, t: "Only bet what you can lose", d: "Never gamble with money needed for essentials. Treat any stake as the price of entertainment." },
              { icon: <Clock className="w-5 h-5" />, t: "Set time & money limits", d: "Decide your budget and session length before you start — and honour them, win or lose." },
              { icon: <Ban className="w-5 h-5" />, t: "Never chase losses", d: "No system reverses the house edge. Chasing losses is the fastest route to real harm." },
              { icon: <HeartHandshake className="w-5 h-5" />, t: "Ask for help early", d: "Support is free and confidential. Reaching out is a sign of strength, not failure." },
            ].map((p) => (
              <div key={p.t} className="border border-white/10 bg-card p-6">
                <div className="w-10 h-10 grid place-items-center border border-gold/30 text-gold mb-4">{p.icon}</div>
                <h3 className="font-display text-lg text-white">{p.t}</h3>
                <p className="mt-2 text-sm text-white/55 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Warning signs */}
        <section>
          <p className="overline text-gold mb-6">▸ Warning Signs</p>
          <div className="border border-crimson-bright/30 bg-crimson/10 p-7">
            <p className="text-white/70 mb-5 leading-relaxed">
              Gambling may be becoming a problem if you recognise any of these. Any one of them is worth taking
              seriously:
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {SIGNS.map((s) => (
                <li key={s} className="flex gap-2.5 text-sm text-white/75">
                  <span className="text-crimson-bright mt-0.5">▸</span> {s}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Resources */}
        <section>
          <p className="overline text-gold mb-6">▸ Get Support</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {RESOURCES.map((r) => (
              <a
                key={r.name}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`rg-resource-${r.name.toLowerCase()}`}
                className="group border border-white/10 bg-card p-6 hover:border-gold/40 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-xl text-white group-hover:text-gold transition-colors">{r.name}</h3>
                  <ExternalLink className="w-4 h-4 text-white/30 group-hover:text-gold transition-colors" />
                </div>
                <span className="overline text-white/40">{r.region}</span>
                <p className="mt-3 text-sm text-white/55 leading-relaxed">{r.desc}</p>
              </a>
            ))}
          </div>

          <div className="mt-6 border border-gold/30 bg-[#0a0c0c] p-6 flex items-center gap-4">
            <PhoneCall className="w-6 h-6 text-gold shrink-0" />
            <div>
              <p className="overline text-gold">National Gambling Helpline (UK)</p>
              <p className="mt-1 font-mono text-xl text-white">0808 8020 133</p>
              <p className="text-sm text-white/50">Free, confidential, 24 hours a day.</p>
            </div>
          </div>
        </section>

        <p className="text-sm text-white/40 leading-relaxed">
          This site does not accept wagers and is not affiliated with any operator. Content is for education
          only. Return to the{" "}
          <Link to="/dossier" className="text-gold hover:underline">
            dossier
          </Link>{" "}
          or read{" "}
          <Link to="/dossier/setting-stop-loss-stop-win" className="text-gold hover:underline">
            Setting Stop-Loss and Stop-Win Limits
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
